import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { DocumentMinimalData, ProviderContractDatabase } from "../../../../types";
import usePostProviderContract from "../../api/usePostProviderContract";
import usePutProviderContract from "../../api/usePutProviderContract";
import { useEffect, useRef, useState } from "react";
import { isSupabaseError } from "../../../../constants/supabaseErrors";
import { useNavigate } from "react-router-dom";
import { useSpacePath } from "../../../../hooks/useSpacePath";
import { useSpace } from "../../../../hooks/useSpace";
import { useChangeDocumentsOfResource } from "../../../documents/hooks/useChangeDocumentsOfResource";
import { useGetSpaceAISettings } from "../../../inbox/api/useGetSpaceAISettings";
import useGetProviderById from "../../api/useGetProviderById";
import { useAnalyzeContract } from "../../api/useAnalyzeContract";

export const formSchema = z.object({
  name: z.string().min(1, 'Ce champ est obligatoire'),
  provider_id: z.string(),
  start_at: z.date().nullable().transform(val => val ? val.toISOString() : null),
  end_at: z.date().nullable().transform(val => val ? val.toISOString() : null),
  description: z.string().nullable().optional(),
  price: z.string().nullable().optional(),
  period_of_notice: z.string().nullable().optional(),
  duration_of_renewal: z.number().nullable().optional(),
  has_automatic_renewal: z.boolean()
}).transform((data) => ({
  ...data,
  duration_of_renewal: data.has_automatic_renewal ? data.duration_of_renewal : null,
  end_at: data.has_automatic_renewal ? null : data.end_at,
  has_automatic_renewal: undefined
}));

export type FormValues = z.input<typeof formSchema>;
export type FormValuesOutput = z.infer<typeof formSchema>;

export type ContractModalStep = 'dropzone' | 'analyzing' | 'form';

export function parseIsoDate(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return null;
}

export type InitialContractFormValues = {
  name?: string | null;
  provider_id?: string | null;
  start_at?: Date | null;
  end_at?: Date | null;
  description?: string | null;
  price?: string | null;
  period_of_notice?: string | null;
  duration_of_renewal?: number | null;
  has_automatic_renewal?: boolean;
};

export interface useAddUpdateContractFormProps {
  providerId: string;
  contract?: ProviderContractDatabase;
  initialValues?: InitialContractFormValues | null;
  initialDocument?: DocumentMinimalData | null;
  isAiPreFilled?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function useAddUpdateContractForm({
  providerId,
  contract,
  initialValues,
  initialDocument,
  isAiPreFilled: initialIsAiPreFilled,
  onClose,
  onSuccess,
}: useAddUpdateContractFormProps) {
  const navigate = useNavigate();
  const spacePath = useSpacePath();
  const { currentSpace } = useSpace();
  const { data: isAiEnabled = false, isLoading: isAiSettingsLoading } = useGetSpaceAISettings({ spaceId: currentSpace?.id });
  const { data: provider } = useGetProviderById(providerId);
  const analyzeContractMutation = useAnalyzeContract();

  const { mutateAsync: postContract, isPending: isPosting } = usePostProviderContract();
  const { mutateAsync: updateContract, isPending: isUpdating } = usePutProviderContract();
  const { uploadPendingFiles, isPending: isUploading } = useChangeDocumentsOfResource();

  const [serverError, setServerError] = useState<string | null>(null);
  const [analysisWarning, setAnalysisWarning] = useState<string | null>(null);
  const [isAiPreFilled, setIsAiPreFilled] = useState<boolean>(() => {
    return Boolean(initialIsAiPreFilled ?? initialValues);
  });
  const [analyzingFile, setAnalyzingFile] = useState<File | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  // Step state: 'dropzone', 'analyzing', or 'form'
  const [step, setStep] = useState<ContractModalStep>(() => {
    if (contract || initialValues || initialDocument) return 'form';
    return 'dropzone';
  });

  // If AI is disabled or when modifying an existing contract, always display the standard form
  useEffect(() => {
    if (contract || initialValues || initialDocument) {
      setStep('form');
      return;
    }
    if (!isAiSettingsLoading && !isAiEnabled && step === 'dropzone') {
      setStep('form');
    }
  }, [contract, isAiEnabled, isAiSettingsLoading, step, initialValues, initialDocument]);

  // The contract file: an uploaded/linked document, resolved to a URL on submit.
  const [document, setDocument] = useState<DocumentMinimalData | null>(() => {
    if (initialDocument) return initialDocument;
    if (contract?.file_url) {
      return {
        id: contract.document_id ?? crypto.randomUUID(),
        name: contract.name,
        file_url: contract.file_url,
        type: 'Contrat',
      };
    }
    return null;
  });

  const form = useForm<FormValues, unknown, FormValuesOutput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: contract?.name ?? initialValues?.name ?? "",
      provider_id: providerId || initialValues?.provider_id || "",
      start_at: contract?.start_at
        ? new Date(contract.start_at)
        : (initialValues?.start_at ?? null),
      end_at: contract?.end_at
        ? new Date(contract.end_at)
        : (initialValues?.end_at ?? null),
      description: contract?.description ?? initialValues?.description ?? null,
      price: contract?.price ?? initialValues?.price ?? null,
      period_of_notice: contract?.period_of_notice ?? initialValues?.period_of_notice ?? null,
      duration_of_renewal: contract?.duration_of_renewal ?? initialValues?.duration_of_renewal ?? (initialValues?.has_automatic_renewal ? 1 : null),
      has_automatic_renewal: contract ? (contract.duration_of_renewal !== null) : (initialValues?.has_automatic_renewal ?? false),
    },
  });

  async function handleStartAnalysis(file: File) {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setAnalyzingFile(file);
    setStep('analyzing');
    setAnalysisWarning(null);

    try {
      const analyzed = await analyzeContractMutation.mutateAsync({
        file,
        spaceId: currentSpace?.id ?? '',
        providerName: provider?.name,
        signal: controller.signal,
      });

      // Attach file to document state
      setDocument({
        id: crypto.randomUUID(),
        name: analyzed.name || file.name,
        file_url: null,
        type: 'Contrat',
        pendingFile: file,
      });

      // Pre-fill form fields
      if (analyzed.name) {
        form.setValue('name', analyzed.name);
      } else if (!form.getValues('name')) {
        form.setValue('name', file.name.replace(/\.[^/.]+$/, ''));
      }

      if (analyzed.start_at) {
        const parsedStart = parseIsoDate(analyzed.start_at);
        if (parsedStart) {
          form.setValue('start_at', parsedStart);
        }
      }

      form.setValue('has_automatic_renewal', analyzed.has_automatic_renewal);

      if (analyzed.has_automatic_renewal) {
        if (analyzed.duration_of_renewal) {
          form.setValue('duration_of_renewal', analyzed.duration_of_renewal);
        }
        form.setValue('end_at', null);
      } else {
        form.setValue('duration_of_renewal', null);
        if (analyzed.end_at) {
          const parsedEnd = parseIsoDate(analyzed.end_at);
          if (parsedEnd) {
            form.setValue('end_at', parsedEnd);
          }
        }
      }

      if (analyzed.period_of_notice) {
        form.setValue('period_of_notice', analyzed.period_of_notice);
      }

      if (analyzed.price) {
        form.setValue('price', analyzed.price);
      }

      if (analyzed.description) {
        form.setValue('description', analyzed.description);
      }

      setIsAiPreFilled(true);
      setStep('form');
    } catch (error: unknown) {
      if (controller.signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
        return;
      }

      // Keep document with file so the user does not lose their uploaded file
      setDocument({
        id: crypto.randomUUID(),
        name: file.name,
        file_url: null,
        type: 'Contrat',
        pendingFile: file,
      });

      const message = error instanceof Error
        ? error.message
        : "L'analyse automatique n'a pas pu aboutir. Vous pouvez remplir les informations manuellement.";
      setAnalysisWarning(message);
      setStep('form');
    }
  }

  function handleSkipToManual() {
    setStep('form');
  }

  function handleCancelAnalysis() {
    abortControllerRef.current?.abort();
    if (analyzingFile) {
      setDocument({
        id: crypto.randomUUID(),
        name: analyzingFile.name,
        file_url: null,
        type: 'Contrat',
        pendingFile: analyzingFile,
      });
    }
    setStep('form');
  }

  async function handleSubmit(data: FormValuesOutput) {
    setServerError(null);

    try {
      // Push any pending file to Drive, then persist its resolved URL.
      const [resolved] = document ? await uploadPendingFiles([{ ...document, name: data.name }], { folderName: 'Contrats' }) : [];
      const file_url = resolved?.file_url ?? null;

      const targetProviderId = data.provider_id || providerId;

      if (contract?.id) {
        await updateContract({
          providerId: targetProviderId,
          contractId: contract.id,
          currentDocumentId: contract.document_id ?? null,
          command: { ...data, file_url },
        });
      } else {
        await postContract({
          providerId: targetProviderId,
          command: { ...data, file_url },
        });
        if (onSuccess) {
          onSuccess();
        } else {
          navigate(spacePath(`/providers/${targetProviderId}/contracts`));
        }
      }
      onClose();
    } catch (errors: unknown) {
      if (isSupabaseError(errors)) {
        setServerError(errors.message);

        return;
      }

      setServerError('Une erreur est survenue.');
    }
  }

  return {
    form,
    document,
    setDocument,
    onSubmit: form.handleSubmit(handleSubmit),
    isSubmitting: isPosting || isUpdating || isUploading,
    serverError,
    step,
    isAiEnabled,
    isAiPreFilled,
    analysisWarning,
    analyzingFileName: analyzingFile?.name ?? '',
    handleStartAnalysis,
    handleSkipToManual,
    handleCancelAnalysis,
  };
}
